import { IUser } from '@ViewModels';
import * as React from 'react';
import { useLeadServedRoleAutocompleteQuery } from '../../../../queries/LeadServed/useLeadServedRoleAutocompleteQuery';
import { usePhoneCallCategoriesQuery } from '../../../../queries/PhoneCall/usePhoneCallCategoriesQuery';
import { useTagAutocompleteV2Query } from '../../../../queries/Tag/useTagAutocompleteV2Query';
import {
	Combobox,
	ComboboxContent,
	ComboboxEmpty,
	ComboboxInput,
	ComboboxItem,
	ComboboxList,
} from '../../../../web/components-v2/Combobox';
import { Field, FieldLabel } from '../../../../web/components-v2/Field';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../../web/components-v2/Select';
import {
	ManagerCombobox,
	ManagerComboboxStatus,
	UserComboboxAvatarItem,
} from '../../../../web/components/UserCombobox';
import { useTeamAutocompleteQuery } from '../../../queries/Aida/useTeamAutocomplete';
import { CALL_TYPE_OPTIONS, CallTypeFilter } from '../../../entities/LeadView/useLeadView';
import { CallLogUrlFilters, RECORDING_CHANNEL_OPTIONS, TRANSCRIPTION_OPTIONS } from '../callLogFilters';
import { styleSheet } from './styles';

const ANY = 'any';

interface IFilterSelectProps<T extends string> {
	id: string;
	label: string;
	anyLabel: string;
	value: T | null;
	options: { label: string; value: T }[];
	onChange: (value: T | null) => void;
}

function FilterSelect<T extends string>({ id, label, anyLabel, value, options, onChange }: IFilterSelectProps<T>) {
	const items = [{ label: anyLabel, value: ANY }, ...options];
	return (
		<Field styles={[styleSheet.field]}>
			<FieldLabel htmlFor={id}>{label}</FieldLabel>
			<Select
				items={items}
				value={value ?? ANY}
				onValueChange={(next: string) => onChange(!next || next === ANY ? null : (next as T))}
			>
				<SelectTrigger id={id} styles={[styleSheet.selectTrigger]}>
					<SelectValue placeholder={anyLabel} />
				</SelectTrigger>
				<SelectContent>
					{items.map(item => (
						<SelectItem key={item.value} value={item.value}>
							{item.label}
						</SelectItem>
					))}
				</SelectContent>
			</Select>
		</Field>
	);
}

const toOptions = (values: string[]) => values.map(value => ({ label: value, value }));

function useSyncedSearch(value: string | null) {
	const [search, setSearch] = React.useState(value ?? '');
	const [synced, setSynced] = React.useState(value);
	if (value !== synced) {
		setSynced(value);
		setSearch(value ?? '');
	}
	return [search, setSearch] as const;
}

interface ISearchFilterProps {
	id: string;
	label: string;
	placeholder: string;
	emptyText: string;
	value: string | null;
	options: string[];
	search: string;
	onSearchChange: (search: string) => void;
	onChange: (value: string | null) => void;
}

function SearchFilter({
	id,
	label,
	placeholder,
	emptyText,
	value,
	options,
	search,
	onSearchChange,
	onChange,
}: ISearchFilterProps) {
	return (
		<Combobox
			items={options}
			value={value}
			inputValue={search}
			onInputValueChange={onSearchChange}
			filter={null}
			onValueChange={(next: unknown) => onChange(typeof next === 'string' && next ? next : null)}
		>
			<Field styles={[styleSheet.field]}>
				<FieldLabel htmlFor={id}>{label}</FieldLabel>
				<ComboboxInput id={id} placeholder={placeholder} showClear />
			</Field>
			<ComboboxContent>
				<ComboboxEmpty>{emptyText}</ComboboxEmpty>
				<ComboboxList>
					{(option: string) => (
						<ComboboxItem key={option} value={option}>
							{option}
						</ComboboxItem>
					)}
				</ComboboxList>
			</ComboboxContent>
		</Combobox>
	);
}

interface IProps {
	filters: CallLogUrlFilters;
	enabled: boolean;
	onChange: (next: Partial<CallLogUrlFilters>) => void;
}

export const SecondaryFilterFields: React.FC<IProps> = ({ filters, enabled, onChange }) => {
	const id = React.useId();
	const [roleSearch, setRoleSearch] = useSyncedSearch(filters.role);
	const [teamSearch, setTeamSearch] = useSyncedSearch(filters.team);
	const [tagSearch, setTagSearch] = useSyncedSearch(filters.tag);
	const rolesQuery = useLeadServedRoleAutocompleteQuery({ enabled, fragment: roleSearch });
	const teamsQuery = useTeamAutocompleteQuery({ enabled, fragment: teamSearch });
	const categoriesQuery = usePhoneCallCategoriesQuery({ enabled });
	const tagsQuery = useTagAutocompleteV2Query({ enabled: enabled && !!tagSearch, query: tagSearch });
	const tagNames = (tagsQuery.data?.pages.flatMap(page => page.values ?? []) ?? [])
		.map(tag => tag.name)
		.filter((name): name is string => !!name);

	return (
		<>
			<ManagerCombobox
				id={filters.manager ?? undefined}
				key={`manager-${filters.manager ?? 'any'}`}
				onValueChange={(user: IUser | null) => {
					onChange({ manager: user?.id ?? null });
				}}
			>
				<Field styles={[styleSheet.field]}>
					<FieldLabel htmlFor={`${id}-manager`}>Manager</FieldLabel>
					<ComboboxInput id={`${id}-manager`} placeholder='Any manager' showClear />
				</Field>
				<ComboboxContent>
					<ManagerComboboxStatus />
					<ComboboxList>{(user: IUser) => <UserComboboxAvatarItem key={user.id} user={user} />}</ComboboxList>
				</ComboboxContent>
			</ManagerCombobox>
			<SearchFilter
				id={`${id}-role`}
				label='Role'
				placeholder='Any role'
				emptyText='No roles found.'
				value={filters.role}
				options={rolesQuery.data ?? []}
				search={roleSearch}
				onSearchChange={setRoleSearch}
				onChange={role => onChange({ role })}
			/>
			<SearchFilter
				id={`${id}-team`}
				label='Team'
				placeholder='Any team'
				emptyText='No teams found.'
				value={filters.team}
				options={teamsQuery.data ?? []}
				search={teamSearch}
				onSearchChange={setTeamSearch}
				onChange={team => onChange({ team })}
			/>
			<SearchFilter
				id={`${id}-tag`}
				label='Company tag'
				placeholder='Any tag'
				emptyText={tagSearch ? 'No tags found.' : 'Start typing to search tags'}
				value={filters.tag}
				options={tagNames}
				search={tagSearch}
				onSearchChange={setTagSearch}
				onChange={tag => onChange({ tag })}
			/>
			<FilterSelect
				id={`${id}-category`}
				label='Recording category'
				anyLabel='Any category'
				value={filters.category}
				options={toOptions((categoriesQuery.data ?? []).map(category => category.name))}
				onChange={category => onChange({ category })}
			/>
			<FilterSelect
				id={`${id}-transcription`}
				label='Transcription'
				anyLabel='Any status'
				value={filters.transcription}
				options={TRANSCRIPTION_OPTIONS}
				onChange={transcription => onChange({ transcription })}
			/>
			<FilterSelect
				id={`${id}-channels`}
				label='Recording channels'
				anyLabel='Any channels'
				value={filters.channels}
				options={RECORDING_CHANNEL_OPTIONS}
				onChange={channels => onChange({ channels })}
			/>
			<FilterSelect
				id={`${id}-call-type`}
				label='Call type'
				anyLabel='Any call type'
				value={filters.callType}
				options={CALL_TYPE_OPTIONS.filter(option => option.value !== CallTypeFilter.All)}
				onChange={callType => onChange({ callType })}
			/>
		</>
	);
};
